import React from "react";
import { ArrowRight, BookOpen, ShieldCheck, Sparkles, CheckCircle2, Star, Users, Award, Globe, Search, FileText, RefreshCw, Languages, MessageCircle, Lock, TrendingUp, Quote, ChevronRight, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { usePopup } from "../context/PopupContext";
import SectionCarousel from "../components/SectionCarousel";
import CTABanner from "../components/CTABanner";
import ChatButton from "../components/ChatButton";
import SignupForm from "../components/SignupForm";
import TestimonialCarousel from "../components/TestimonialCarousel";
import PageMeta from "../components/PageMeta";
import { Link } from "react-router-dom";

const FEATURES = [
  { icon: ShieldCheck, title: "Policy Compliant", desc: "Every page follows Wikipedia's notability, neutrality and sourcing policies to the letter." },
  { icon: Award, title: "10+ Years Experience", desc: "A senior team of editors, researchers and writers trained in Wikipedia conventions." },
  { icon: Lock, title: "100% Confidential", desc: "NDAs, private communications and no traces left to your brand during editing." },
  { icon: TrendingUp, title: "Long-Term Visibility", desc: "Pages built to survive edit reviews and rank in Google's Knowledge Graph." },
  { icon: Globe, title: "Multilingual Publishing", desc: "Translate and republish your page across 280+ Wikipedia language editions." },
  { icon: RefreshCw, title: "Ongoing Maintenance", desc: "We monitor, update and defend your page against unwanted edits and vandalism." },
];

const SERVICES = [
  { icon: BookOpen, title: "Wiki Creation & Setup", desc: "End-to-end creation of Wikipedia pages for individuals and companies.", to: "/services" },
  { icon: FileText, title: "Content Writing & Editing", desc: "Expertly written, neutral-tone articles backed by verifiable citations.", to: "/services" },
  { icon: Search, title: "Compliance & Notability", desc: "Pre-publish notability assessment to maximise acceptance probability.", to: "/services" },
  { icon: Languages, title: "Maintenance & Translation", desc: "Continuous updates, monitoring and multi-language translations.", to: "/services" },
];

const STEPS = [
  { num: "01", title: "Notability Review", desc: "We evaluate your story, sources and press coverage to gauge Wikipedia eligibility." },
  { num: "02", title: "Research & Drafting", desc: "Our writers build a neutral-tone draft grounded in reliable third-party citations." },
  { num: "03", title: "Submission & Review", desc: "We publish to Articles for Creation and handle reviewer feedback until acceptance." },
  { num: "04", title: "Monitoring & Updates", desc: "We watch your page 24/7 and update it as your story evolves." },
];

const TESTIMONIALS = [
  { name: "Alicia Hernandez", role: "Founder, Northwind Ventures", quote: "Prime Wiki Services took a messy draft and turned it into a page that actually survived review. They understood Wikipedia culture in a way no one else did.", stars: 5 },
  { name: "Karim Al-Rashid", role: "CEO, Cedar Labs", quote: "After two failed submissions with other agencies, this team got our company page accepted in four weeks. Worth every penny.", stars: 5 },
  { name: "Emma Whitfield", role: "Author & Speaker", quote: "I was nervous about the neutrality requirement. Their writers nailed the tone and my page has been stable for 18 months.", stars: 5 },
  { name: "Rohan Mehta", role: "Partner, Verve Capital", quote: "Clear communication, transparent timelines and a page we're genuinely proud of. The best credibility investment we've made.", stars: 5 },
];

const STATS = [
  { value: "400+", label: "Pages Published" },
  { value: "92%", label: "Acceptance Rate" },
  { value: "42", label: "Languages Covered" },
  { value: "11yrs", label: "Avg. Editor Tenure" },
];

const FAQ = [
  { q: "Do you guarantee my page will be published?", a: "We don't guarantee approval — no ethical agency can. We do guarantee a thorough notability review, transparent communication and full refund options if your topic doesn't qualify." },
  { q: "How long does the process take?", a: "Typical timelines are 4–10 weeks from kickoff to publication, depending on the availability of sources and Wikipedia reviewer queues." },
  { q: "Is this really confidential?", a: "Yes. We work under NDA, use neutral editor accounts and never link any edits publicly back to your brand." },
  { q: "What makes your pages survive edits?", a: "We build pages the way seasoned Wikipedians do — with reliable secondary sources, neutral tone, and ongoing monitoring to defend against vandalism." },
  { q: "Do you handle translations?", a: "Yes — after English publication we can translate and cross-publish into 280+ Wikipedia language editions." },
];

export default function Home() {
  const { openPopup } = usePopup();
  return (
    <div className="overflow-hidden" data-testid="home-page">
      <PageMeta
        title="Prime Wiki Services — Wikipedia Page Creation Services for Individuals & Brands"
        description="Professional Wikipedia page creation, editing, compliance and maintenance services. 400+ pages published, 92% AfC acceptance rate. Free notability review for qualifying subjects."
        keywords="wikipedia page creation, wikipedia writers, wikipedia services, create wikipedia page, wikipedia editors, wikipedia agency, notability review, wikipedia biography, wikipedia company page"
      />
      {/* 1. HERO */}
      <section className="relative mwb-hero-bg">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background: "radial-gradient(1200px 600px at 80% -10%, rgba(16,185,129,0.14) 0%, transparent 60%), radial-gradient(800px 400px at 0% 0%, rgba(59,130,246,0.10) 0%, transparent 60%)",
          }}
        />
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-20">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> Trusted by 400+ individuals & brands
            </span>
            <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] tracking-tight text-[#0A2540] sm:text-5xl lg:text-6xl">
              Wikipedia pages that{" "}
              <span className="relative inline-block">
                <span className="relative z-10">last</span>
                <span className="absolute inset-x-0 bottom-1 z-0 h-3 bg-emerald-200/70" />
              </span>
              . Built by senior editors who understand the policies.
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              From notability assessment to post-publishing maintenance — we create, defend and translate Wikipedia pages that survive review and boost your credibility in Google's Knowledge Graph.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                onClick={openPopup}
                className="h-12 rounded-full px-6 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]"
                style={{ background: "linear-gradient(135deg, #0A2540 0%, #10B981 170%)" }}
                data-testid="hero-cta-primary"
              >
                Create Wiki Page <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <ChatButton className="h-12 rounded-full border border-slate-200 bg-white px-6 text-sm font-semibold text-[#0A2540] hover:border-slate-300">
                <MessageCircle className="h-4 w-4" /> Chat with a Specialist
              </ChatButton>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Free notability review</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> NDA on every engagement</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Money-back if not eligible</span>
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <SignupForm testIdPrefix="hero" />
          </div>
        </div>
      </section>

      {/* 1b. INFORMATIVE — Below hero */}
      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8" data-testid="home-intro-section">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Why it matters</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">
              Wikipedia is the quietest form of credibility on the internet.
            </h2>
            <p className="mt-4 text-base text-slate-600">
              When a journalist, investor or customer searches your name, Wikipedia is almost always the first independent source they read. Yet more than <span className="font-semibold text-[#0A2540]">70% of attempted pages</span> are deleted within weeks because of notability, sourcing or tone violations.
            </p>
            <p className="mt-3 text-base text-slate-600">
              Our editors have spent a decade studying what survives review — and what doesn't. Every page we publish is designed around that hard-earned pattern.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={openPopup} className="h-11 rounded-full bg-[#0A2540] px-6 text-sm font-semibold text-white hover:bg-[#123B6E]" data-testid="intro-cta-primary">
                Reserve my discount
              </Button>
              <Link to="/services">
                <Button variant="outline" className="h-11 rounded-full px-6 text-sm font-semibold">Explore our services</Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { k: "Notability", v: "Does a reliable, independent source consider your subject significant? If yes, we can build. If not, we'll tell you.", icon: Award },
                { k: "Verifiability", v: "Every claim tied to a secondary source — no press releases, no self-published bios, no interviews.", icon: ShieldCheck },
                { k: "Neutrality", v: "No promotional superlatives. No marketing copy. Only dry, encyclopedic prose that survives reviewer scrutiny.", icon: FileText },
                { k: "No original research", v: "We synthesize what others have already published. Wikipedia is not where stories start — it's where they're consolidated.", icon: BookOpen },
              ].map((p) => (
                <div key={p.k} className="rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-[0_16px_36px_-20px_rgba(16,185,129,0.4)]">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif mt-3 text-lg text-[#0A2540]">{p.k}</h3>
                  <p className="mt-1.5 text-sm text-slate-600">{p.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR — removed per spec */}

      {/* 3. WHY US */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="why-us">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Why Prime Wiki Services</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Built by Wikipedians. Backed by policy.</h2>
          <p className="mt-3 text-base text-slate-600">We aren't a content farm. Every engagement is handled by senior editors with deep knowledge of Wikipedia's bureaucracy, notability thresholds and source hierarchy.</p>
        </div>
        <SectionCarousel
          className="mt-10"
          items={FEATURES}
          gridCols="md:grid-cols-2 lg:grid-cols-3"
          renderItem={(f) => (
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_20px_40px_-20px_rgba(16,185,129,0.4)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#0A2540]/5 text-[#0A2540] group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-serif mt-4 text-lg text-[#0A2540]">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
            </div>
          )}
        />
      </section>

      {/* 4. SERVICES PREVIEW */}
      <section className="bg-[#0A2540] py-20 text-white" data-testid="services-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Services</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold sm:text-4xl">Everything you need — one Wikipedia specialist.</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200">
              Explore all services <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <SectionCarousel
            className="mt-10"
            items={SERVICES}
            gridCols="md:grid-cols-2 lg:grid-cols-4"
            renderItem={(s) => (
              <Link to={s.to} className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/10">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/20 text-emerald-300">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif mt-5 text-xl">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm text-white/70">{s.desc}</p>
                <span className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-300">Learn more <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            )}
          />
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="process-section">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Our Process</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Four focused phases, zero surprises.</h2>
        </div>
        <SectionCarousel
          className="mt-10"
          items={STEPS}
          gridCols="md:grid-cols-2 lg:grid-cols-4"
          renderItem={(s, i) => (
            <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-6">
              <div className="font-serif text-5xl font-semibold text-emerald-500/30">{s.num}</div>
              <h3 className="font-serif mt-3 text-lg text-[#0A2540]">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
              {i < 3 ? (
                <ArrowRight className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-slate-300 lg:block" />
              ) : null}
            </div>
          )}
        />
      </section>

      {/* 6. STATS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="stats-section">
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 sm:p-12">
          <div className="grid gap-8 text-center sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-4xl font-semibold text-[#0A2540] sm:text-5xl">{s.value}</div>
                <div className="mt-2 text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. WHY WIKIPEDIA MATTERS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="why-wikipedia">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Why Wikipedia?</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">The most trusted page on the internet about you.</h2>
            <p className="mt-4 text-base text-slate-600">Wikipedia is one of the top-ranked results on Google for personal and brand names. A well-maintained page directly feeds Google's Knowledge Panel, Bing entities and Siri/Alexa answers.</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Instant credibility for investor, press and customer research",
                "Drives Google Knowledge Panel and Rich Results visibility",
                "A permanent, policy-protected biographical record",
                "Third-party signal trusted by journalists and analysts",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1491841550275-ad7854e35ca6?auto=format&fit=crop&w=1200&q=80"
                alt="Open notebook with handwritten research notes and a fountain pen beside it"
                className="h-[360px] w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl md:block">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Built on verified sources</div>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Reuters, NYT, WSJ cited</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Peer-reviewed references</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Neutral policy-safe tone</span></div>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg bg-[#0A2540] px-3 py-2 text-xs text-white">
                <span>Reliability score</span>
                <span className="font-serif text-base text-emerald-300">9.4 / 10</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. CASE STUDIES */}
      <section className="bg-slate-50 py-20" data-testid="case-studies">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Case Studies</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Recent pages we've helped ship.</h2>
          </div>
          <SectionCarousel
            className="mt-10"
            items={[
              { tag: "Tech Founder", title: "Approved in 28 days", desc: "Series-B founder with fragmented press coverage — consolidated 17 independent sources into a policy-proof page." },
              { tag: "Pharma Brand", title: "Multilingual roll-out", desc: "English publication translated to 8 languages within 6 weeks of initial approval." },
              { tag: "Author", title: "Restored after deletion", desc: "Page previously deleted twice; rebuilt from scratch with stronger sourcing — live for 22 months." },
            ]}
            gridCols="md:grid-cols-2 lg:grid-cols-3"
            renderItem={(c) => (
              <div className="h-full rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{c.tag}</span>
                <h3 className="font-serif mt-4 text-xl text-[#0A2540]">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{c.desc}</p>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Anonymised for confidentiality</span>
                  <Lock className="h-3.5 w-3.5 text-slate-400" />
                </div>
              </div>
            )}
          />
        </div>
      </section>

      {/* 9. NOTABILITY CHECK */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="notability-check">
        <div className="grid items-center gap-10 rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Free Check</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Are you Wikipedia-notable?</h2>
            <p className="mt-4 text-base text-slate-600">Get a free 30-minute notability review. We'll audit your press coverage, third-party citations and public record against Wikipedia's GNG and BIO guidelines.</p>
            <Button onClick={openPopup} className="mt-6 h-11 rounded-full bg-[#0A2540] px-6 text-sm font-semibold text-white hover:bg-[#123B6E]" data-testid="notability-cta">
              Request Free Review
            </Button>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#123B6E] p-6 text-white">
            <div className="space-y-4">
              {["Do you have 3+ independent press features?","Is there a reliable secondary source about you?","Has your work been cited by journalists or analysts?","Are there public records tied to your role or company?"].map((q, i) => (
                <label key={i} className="flex items-start gap-3 text-sm">
                  <span className="grid h-5 w-5 place-items-center rounded border border-white/30 text-[11px]">{i+1}</span>
                  <span>{q}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 10. TESTIMONIALS (shared) */}
      <TestimonialCarousel />

      {/* 11. SOURCES MATTER (replaces pricing) */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="sources-section">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">The Sourcing Advantage</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Why the sources decide everything.</h2>
          <p className="mt-3 text-base text-slate-600">Wikipedia lives and dies by sources. We don't rely on press releases, founder interviews or sponsored content. We hunt for the hard-to-find independent reporting that reviewers can't argue with.</p>
        </div>
        <SectionCarousel
          className="mt-10"
          items={[
            { tag: "Tier 1", title: "Major outlets", desc: "Reuters, AP, BBC, NYT, WSJ, FT, Bloomberg — the gold standard." },
            { tag: "Tier 2", title: "Trade & industry", desc: "Respected vertical publications with editorial independence." },
            { tag: "Tier 3", title: "Academic & books", desc: "Peer-reviewed journals, university press, established authors." },
          ]}
          gridCols="md:grid-cols-3"
          renderItem={(s) => (
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
              <span className="inline-flex rounded-full bg-[#0A2540]/5 px-3 py-1 text-xs font-semibold text-[#0A2540]">{s.tag}</span>
              <h3 className="font-serif mt-4 text-xl text-[#0A2540]">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
            </div>
          )}
        />
      </section>

      {/* 12. FAQ */}
      <section className="bg-slate-50 py-20" data-testid="faq-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            {/* Left — questions & answers */}
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">FAQ</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Questions, answered.</h2>
              <p className="mt-3 max-w-xl text-base text-slate-600">Everything people ask us before they reach out — on sources, timelines, confidentiality and what happens if a page is ever challenged.</p>
              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {FAQ.map((item, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-6">
                    <AccordionTrigger className="text-left text-base font-semibold text-[#0A2540]">{item.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Right — relevant image */}
            <div className="lg:col-span-5">
              <div className="relative h-full min-h-[440px] overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=1200&q=80"
                  alt="Two colleagues discussing and answering each other's questions at a desk"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Still unsure?</div>
                  <div className="mt-1 font-serif text-base text-[#0A2540]">Speak to a senior Wikipedian — free 20-minute consult, no pressure.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 13. PRESS MENTIONS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 text-center" data-testid="press-section">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Press & Mentions</p>
        <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Our team's work has been referenced in</h2>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-5 text-slate-400">
          {["The Verge","Inc.","Fast Company","HBR","Axios","Protocol","Fortune","Business Insider"].map((n) => (
            <span key={n} className="font-serif text-xl">{n}</span>
          ))}
        </div>
      </section>

      {/* 14. CTA BANNER */}
      <CTABanner />

      {/* 15. FINAL TRUST SECTION */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="final-trust">
        <div className="grid gap-10 md:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "NDA on every project", desc: "Your name is never linked to our agency in any Wikipedia activity." },
            { icon: Users, title: "Senior editors only", desc: "No juniors. No outsourcing. Every page is touched by a 10-year+ Wikipedian." },
            { icon: Award, title: "Refund guarantee", desc: "If we determine you're not notable, we refund the full engagement fee." },
          ].map((x) => (
            <div key={x.title} className="rounded-2xl border border-slate-200 p-7">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                <x.icon className="h-5 w-5" />
              </div>
              <h3 className="font-serif mt-4 text-xl text-[#0A2540]">{x.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{x.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-6">
          <div>
            <h3 className="font-serif text-xl text-[#0A2540]">Prefer to talk first?</h3>
            <p className="mt-1 text-sm text-slate-600">Book a confidential 20-minute call with a Wikipedia specialist — no commitment.</p>
          </div>
          <div className="flex gap-2">
            <a href="tel:+18774691642" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#0A2540] hover:border-slate-300"><Phone className="h-4 w-4" /> Call us</a>
            <a href="mailto:info@Primewikiservices.com" className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#123B6E]"><Mail className="h-4 w-4" /> Email us</a>
          </div>
        </div>
      </section>

      <div className="h-6" />
    </div>
  );
}
